import { Component, Input } from '@angular/core';

/**
 * Placeholder cards for a rail or grid that is still loading.
 *
 * Lifted from the wallpaper listing, which was the only page that had one, so the
 * shape and the pulse are the house style rather than a new invention. One
 * component instead of that block copied per template: the home rails, the PDP's
 * Similar rail, the category and space grids all wait on the same kind of request,
 * and a placeholder drawn slightly differently on each is how a site starts
 * feeling unfinished.
 *
 * The host is `display: contents`, so each placeholder becomes a direct child of
 * the CALLER's grid and inherits its columns and gaps. That matters: a rail is 6
 * across and a listing is 2–3, and a component that imposed its own grid would be
 * wrong on one of them.
 */
@Component({
  selector: 'app-card-skeleton',
  template: `
    <div *ngFor="let slot of slots" class="animate-pulse" [ngClass]="cardClass">
      <div class="rounded-lg md:rounded-xl bg-gray-200" [ngStyle]="{ 'aspect-ratio': aspect }"></div>
      <div *ngIf="showText" class="mt-2 md:mt-3 space-y-2">
        <div class="h-3 bg-gray-200 rounded w-3/4"></div>
        <div class="h-2 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
  `,
  styles: [':host { display: contents; }'],
})
export class CardSkeletonComponent {
  /**
   * How many placeholders to draw.
   *
   * Callers pass their OWN page size (`[count]="pageSize"`), not a literal, so the
   * grid holds exactly the space the answer will fill and does not jump when it
   * lands. Change a page size in one place and its skeleton follows.
   *
   * Built into a fixed array on write rather than in a getter: `*ngFor` over a
   * freshly-allocated array every change-detection pass would rebuild the DOM on
   * each tick, which is a strange way to make a loading state feel smooth.
   */
  @Input()
  set count(n: number) {
    this.slots = new Array(Math.max(0, Math.min(48, Number(n) || 0)));
  }

  /** Card shape, as a CSS aspect-ratio. Rails are square, listing cards are 4/3. */
  @Input() aspect = '1 / 1';

  /** Off for a bare image rail that carries no name or price under the card. */
  @Input() showText = true;

  /**
   * Sizing classes for one placeholder — pass the caller's own card classes.
   *
   * A grid child needs none, but a horizontal rail's cards carry their width
   * (`flex-shrink-0 w-[50vw] sm:w-[250px] …`) and without it every placeholder
   * would collapse to nothing. Taking them as an input keeps this component
   * layout-agnostic instead of hardcoding one page's rail.
   */
  @Input() cardClass = '';

  slots: undefined[] = new Array(6);
}
